/**
 * navigationItems.ts — Issue #228
 *
 * Pure data + helpers for admin-web navigation:
 *   - `NAV_ITEMS`: primary sidebar (rendered by DashboardLayout)
 *   - `CRM_NAV_ITEMS`: CRM sub-nav (rendered by CrmLayout)
 *   - `selectVisibleNavItems`: permission-aware filter used by the layout
 *   - `selectVisibleCrmNavItems`: same, for the CRM sub-nav
 *
 * The icons are typed via lucide-react's component type — this module imports
 * only icon components (which are tiny SVG factories) and pure permission
 * helpers, so the test suite can import it without mounting React providers.
 */
import {
  LayoutDashboard,
  Users,
  FileText,
  Settings,
  ClipboardList,
  CalendarDays,
  ShieldCheck,
  ShieldAlert,
  ListChecks,
  FolderInput,
  Activity,
  MessageSquareWarning,
  IdCard,
  ScanLine,
  Bot,
  MessageSquare,
  Banknote,
  FlaskConical,
  FileSignature,
  Network,
  Boxes,
  ShoppingCart,
  UserMinus,
  Laptop,
  Building2,
  type LucideIcon,
} from 'lucide-react';
import {
  canSeeCrmMenu,
  canSeeChannelPartnerNav,
  canSeeCustomerNav,
  CRM_PERMISSIONS,
  hasAnyPermission,
  hasPermission,
} from '@/lib/crmPermissions';
import { canSeeWorkOrderMenu } from '@/lib/swoPermissions';
import { canSeeComplianceMenu } from '@/lib/compliancePermissions';
import { canSeeTaskCenterMenu } from '@/lib/taskCenterPermissions';
import { canSeeCustomerDocumentMenu } from '@/lib/customerDocumentPermissions';
import { canSeeIntakeAssessmentMenu } from '@/lib/intakeAssessmentPermissions';
import { canSeeQuoteContractMenu } from '@/lib/quoteContractPermissions';
import { canSeeHealthReportPublicationMenu } from '@/lib/healthReportPublicationPermissions';
import { canSeeComplaintMenu } from '@/lib/complaintPermissions';
import { canSeeHrMenu, HR_PERMISSIONS } from '@/lib/hrPermissions';
import { ADMIN_ASSETS_PERMISSIONS } from '@/lib/adminAssetsPermissions';
import { canSeeAdminAffairsMenu } from '@/lib/adminAffairsPermissions';
import { canSeeAdminProcurementMenu } from '@/lib/adminProcurementPermissions';
import { canSeeScanMenu } from '@/lib/scanPermissions';
import { canSeeAiModelCenterMenu } from '@/lib/aiModelCenterPermissions';
import { canSeeSmsAdminMenu } from '@/lib/smsPermissions';
import { canSeeFinanceMenu } from '@/lib/financePermissions';
import { canSeeInventoryMenu } from '@/lib/inventoryPermissions';
import { canSeeProcurementMenu } from '@/lib/procurementPermissions';
import { canSeeFollowUpMenu } from '@/lib/followUpPermissions';
import { PROPOSAL_PERMISSIONS } from '@/lib/proposalPermissions';
import { SCHEDULING_PERMISSIONS } from '@/lib/schedulingPermissions';
import { CHANNEL_ROI_PERMISSIONS } from '@/lib/channelRoiPermissions';
import { canSeeLabMenu } from '@/lib/labPermissions';
import { canSeeRiskReadModel } from '@/lib/riskReadModelPermissions';

export interface NavItem {
  readonly to: string;
  readonly label: string;
  readonly icon: LucideIcon;
  /** Whether to use startsWith matching for active state. */
  readonly prefixMatch?: boolean;
  /**
   * Optional permission predicate. When provided, the nav item is only
   * rendered if the predicate returns true for the current user's permissions.
   */
  readonly visible?: (permissions: readonly string[]) => boolean;
}

// 第一版导航主入口：覆盖 AI GUIDE 要求的”所有第一版可用模块都能在后台被发现”。
// Issue #291: 顺序按部门/功能区块排列：仪表板 → 财务 → 实验室/库存/扫码 → HR → 客户资源 → KPI/经营 → 系统管理。
export const NAV_ITEMS: readonly NavItem[] = [
  // ── 仪表板 ──────────────────────────────────────────────────────────────────
  { to: '/', label: '仪表板', icon: LayoutDashboard },

  // ── 财务 ────────────────────────────────────────────────────────────────────
  {
    to: '/finance/payment-records',
    label: '财务结算',
    icon: Banknote,
    prefixMatch: true,
    visible: canSeeFinanceMenu,
  },

  // ── 实验室 / 库存 / 扫码 ────────────────────────────────────────────────────
  {
    to: '/lab/batches',
    label: '实验室与回输',
    icon: FlaskConical,
    prefixMatch: true,
    visible: canSeeLabMenu,
  },
  {
    to: '/inventory/consumables',
    label: '物料库存',
    icon: Boxes,
    prefixMatch: true,
    visible: canSeeInventoryMenu,
  },
  {
    to: '/procurement/service-catalogs',
    label: '服务目录与采购',
    icon: ShoppingCart,
    prefixMatch: true,
    visible: canSeeProcurementMenu,
  },
  {
    to: '/scan',
    label: '统一扫码',
    icon: ScanLine,
    prefixMatch: true,
    visible: canSeeScanMenu,
  },

  // ── 人力资源 ────────────────────────────────────────────────────────────────
  {
    to: '/hr/employees',
    label: '员工花名册',
    icon: IdCard,
    prefixMatch: true,
    visible: canSeeHrMenu,
  },
  // Issue #362 — HR credential / certificate expiry + login freeze surface.
  // Visible to anyone holding hr_certificate:read; non-HR roles never see it
  // even if they hold the broad hr:read.
  {
    to: '/hr/certificates',
    label: '资质证照',
    icon: ShieldAlert,
    prefixMatch: true,
    visible: (perms) => hasPermission(perms, HR_PERMISSIONS.CERTIFICATE_READ),
  },
  // Issue #363 — HR offboarding lifecycle + access revoke + handoff checklist.
  // Visible to anyone holding hr_offboarding:read; non-HR roles never see it
  // even if they hold the broad hr:read.
  {
    to: '/hr/offboarding',
    label: '员工离职',
    icon: UserMinus,
    prefixMatch: true,
    visible: (perms) => hasPermission(perms, HR_PERMISSIONS.OFFBOARDING_READ),
  },
  // Issue #364 — Administrative fixed assets surface. Visible to anyone
  // holding admin_assets:read; finance / manage levels gate value column +
  // retire / lost / archive buttons in the page itself.
  {
    to: '/admin/assets',
    label: '行政固定资产',
    icon: Laptop,
    prefixMatch: true,
    visible: (perms) => hasPermission(perms, ADMIN_ASSETS_PERMISSIONS.READ),
  },
  // Issue #365 — Administrative affairs hub. Visible to anyone holding
  // admin_affairs:read, admin_affairs:write or admin_announcements:read so a
  // regular employee can at least see and ack announcements. Detail pages
  // (meeting rooms / bookings / vehicle / seal / announcements) re-check the
  // exact permission for write / approve / manage actions.
  {
    to: '/admin/affairs',
    label: '行政事务',
    icon: Building2,
    prefixMatch: true,
    visible: canSeeAdminAffairsMenu,
  },
  // Issue #366 — Administrative procurement ledger + item movements +
  // dashboard. Visible to anyone holding admin_procurement:read or :write so
  // a regular admin can list rows; finance / approve / manage actions are
  // re-checked inside the page itself.
  {
    to: '/admin/procurement',
    label: '行政采购台账',
    icon: ShoppingCart,
    prefixMatch: true,
    visible: canSeeAdminProcurementMenu,
  },
  {
    to: '/admin/dashboard',
    label: '行政看板',
    icon: LayoutDashboard,
    prefixMatch: true,
    visible: canSeeAdminProcurementMenu,
  },

  // ── 客户资源 ────────────────────────────────────────────────────────────────
  {
    to: '/crm/customers',
    label: '客户与订单',
    icon: Users,
    prefixMatch: true,
    visible: (perms) =>
      canSeeCrmMenu(perms) ||
      hasAnyPermission(perms, [CHANNEL_ROI_PERMISSIONS.PARTNER_READ]),
  },
  {
    to: '/proposals',
    label: '方案与报价',
    icon: FileSignature,
    prefixMatch: true,
    visible: (perms) => hasPermission(perms, PROPOSAL_PERMISSIONS.PROPOSAL_READ),
  },
  {
    to: '/quote-contract/quotes',
    label: '正式报价与合同',
    icon: FileSignature,
    prefixMatch: true,
    visible: canSeeQuoteContractMenu,
  },
  {
    to: '/operations/work-orders',
    label: '服务工单',
    icon: ClipboardList,
    prefixMatch: true,
    visible: canSeeWorkOrderMenu,
  },
  {
    to: '/scheduling/appointments',
    label: '预约排班',
    icon: CalendarDays,
    prefixMatch: true,
    visible: (perms) =>
      hasAnyPermission(perms, [
        SCHEDULING_PERMISSIONS.APPOINTMENT_READ,
        SCHEDULING_PERMISSIONS.SCHEDULE_READ,
      ]),
  },
  {
    to: '/customer-documents',
    label: '客户资料审核',
    icon: FolderInput,
    prefixMatch: true,
    visible: canSeeCustomerDocumentMenu,
  },
  {
    to: '/intake-assessment/records',
    label: '接诊评估',
    icon: ClipboardList,
    prefixMatch: true,
    visible: canSeeIntakeAssessmentMenu,
  },
  {
    to: '/health-report-publications',
    label: '健康报告发布',
    icon: Activity,
    prefixMatch: true,
    visible: canSeeHealthReportPublicationMenu,
  },
  {
    to: '/follow-up/plans',
    label: '随访与疗效',
    icon: Activity,
    prefixMatch: true,
    visible: canSeeFollowUpMenu,
  },
  {
    to: '/complaints',
    label: '投诉与纠纷',
    icon: MessageSquareWarning,
    prefixMatch: true,
    visible: canSeeComplaintMenu,
  },
  {
    to: '/risk/read-model',
    label: '风险事件读模型',
    icon: ShieldAlert,
    prefixMatch: true,
    visible: canSeeRiskReadModel,
  },
  {
    to: '/channel-roi',
    label: '渠道 ROI',
    icon: Network,
    prefixMatch: true,
    visible: (perms) =>
      hasAnyPermission(perms, [
        CHANNEL_ROI_PERMISSIONS.PARTNER_READ,
        CHANNEL_ROI_PERMISSIONS.CONTACT_READ,
        CHANNEL_ROI_PERMISSIONS.LEAD_SOURCE_READ,
        CHANNEL_ROI_PERMISSIONS.ACTIVITY_READ,
        CHANNEL_ROI_PERMISSIONS.COST_READ,
        CHANNEL_ROI_PERMISSIONS.EVALUATION_READ,
        CHANNEL_ROI_PERMISSIONS.COMMISSION_READ,
      ]),
  },

  // ── KPI / 经营看板 ──────────────────────────────────────────────────────────
  { to: '/reports', label: '报告中心', icon: FileText },

  // ── 系统管理 ────────────────────────────────────────────────────────────────
  {
    to: '/compliance',
    label: '政策合规',
    icon: ShieldCheck,
    prefixMatch: true,
    visible: canSeeComplianceMenu,
  },
  {
    to: '/task-center',
    label: '任务中心',
    icon: ListChecks,
    prefixMatch: true,
    visible: canSeeTaskCenterMenu,
  },
  {
    to: '/ai-model-center',
    label: 'AI 模型中心',
    icon: Bot,
    prefixMatch: true,
    visible: canSeeAiModelCenterMenu,
  },
  {
    to: '/sms',
    label: '短信模板与日志',
    icon: MessageSquare,
    prefixMatch: true,
    visible: canSeeSmsAdminMenu,
  },
  { to: '/settings', label: '系统设置', icon: Settings },
];

export function selectVisibleNavItems(
  items: readonly NavItem[],
  permissions: readonly string[],
): readonly NavItem[] {
  return items.filter(
    (item) => item.visible === undefined || item.visible(permissions),
  );
}

// ─── CRM sub-nav ─────────────────────────────────────────────────────────────

export interface CrmNavItem {
  readonly href: string;
  readonly label: string;
  readonly visible: (permissions: readonly string[]) => boolean;
}

export const CRM_NAV_ITEMS: readonly CrmNavItem[] = [
  { href: '/crm/customers', label: '客户管理', visible: canSeeCustomerNav },
  {
    href: '/crm/orders',
    label: 'CRM 订单',
    // Issue #345 audit fix3: OrderList and the backend OrderController gate on
    // `crm.order:read`. Reusing the customer read permission here showed the
    // sub-nav to customer-read-only users who would then land on AccessDenied
    // because the orders list endpoint 403s without `crm.order:read`. Align
    // the nav visibility with the authoritative server gate.
    visible: (perms) => hasPermission(perms, CRM_PERMISSIONS.ORDER_READ),
  },
  { href: '/crm/channel-partners', label: '渠道合作方', visible: canSeeChannelPartnerNav },
  {
    href: '/channel-roi',
    label: '渠道 ROI',
    visible: (perms) =>
      hasAnyPermission(perms, [
        CHANNEL_ROI_PERMISSIONS.PARTNER_READ,
        CHANNEL_ROI_PERMISSIONS.CONTACT_READ,
        CHANNEL_ROI_PERMISSIONS.LEAD_SOURCE_READ,
        CHANNEL_ROI_PERMISSIONS.ACTIVITY_READ,
        CHANNEL_ROI_PERMISSIONS.COST_READ,
        CHANNEL_ROI_PERMISSIONS.EVALUATION_READ,
        CHANNEL_ROI_PERMISSIONS.COMMISSION_READ,
      ]),
  },
];

export function selectVisibleCrmNavItems(
  items: readonly CrmNavItem[],
  permissions: readonly string[],
): readonly CrmNavItem[] {
  return items.filter((item) => item.visible(permissions));
}
